import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Heart, Brain, Moon } from 'lucide-react';
import CTA from '../../components/CTA';

const CraigavonAnxietyStress: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Anxiety Treatment Craigavon | Stress Management Lurgan Portadown</title>
        <meta
          name="description"
          content="Professional anxiety and stress hypnotherapy serving Craigavon from Belfast clinic (25 miles). Evidence-based treatment for anxiety, panic, commuter stress. CNHC registered."
        />
        <meta name="keywords" content="anxiety treatment Craigavon, stress management Lurgan, hypnotherapy Portadown anxiety, panic attacks Craigavon, therapist Craigavon stress, commuter anxiety" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/craigavon-anxiety-stress/" />
        <meta property="og:title" content="Anxiety Treatment Craigavon | Stress Management Lurgan Portadown" />
        <meta property="og:description" content="Professional anxiety and stress hypnotherapy serving Craigavon from Belfast. Evidence-based treatment for anxiety disorders, panic, commuter stress." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Anxiety Treatment Craigavon",
            "description": "CNHC registered hypnotherapy for anxiety and stress, serving Craigavon, Lurgan, and Portadown from Belfast clinic. Specializing in anxiety disorders, panic attacks, commuter stress, and new town disconnection.",
            "url": "https://www.sharonclare-hypnotherapy.com/craigavon-anxiety-stress/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Anxiety and Stress Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Craigavon",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "County Armagh"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How far is Belfast from Craigavon for anxiety treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Belfast is approximately 25 miles from Craigavon via the M1 motorway, taking 30-35 minutes by car. This manageable distance makes regular anxiety treatment appointments entirely practical. Many Craigavon clients find the drive therapeutic - a chance to leave daily stressors behind and transition into a calm, focused mindset before their session."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with commuter stress and anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, solution-focused hypnotherapy is highly effective for commuter-related stress and anxiety. Many Craigavon residents commute to Belfast for work, and the daily M1 journey, traffic pressures, and work-life balance challenges can trigger significant anxiety. Hypnotherapy helps by calming your stress response, building resilience, improving sleep quality, and developing healthier thought patterns around work pressures."
                }
              },
              {
                "@type": "Question",
                "name": "Do you offer online anxiety treatment for Craigavon residents?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. I offer secure online hypnotherapy sessions via video call for Craigavon residents who prefer the convenience of online therapy or cannot make the drive to Belfast on a particular week. Online sessions provide the same professional, evidence-based anxiety treatment as in-person sessions. This option is ideal for clients with caring responsibilities, shift work patterns, or those who want maximum flexibility."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with feeling disconnected or not belonging?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy can be very effective for feelings of disconnection and social anxiety. Living in planned developments like Craigavon, which lack organic community centers, can contribute to isolation and a sense of not quite belonging. While hypnotherapy cannot change your environment, it can help reduce social anxiety, build confidence in forming connections, develop resilience, and create a stronger sense of self-worth that isn't entirely dependent on external community validation."
                }
              },
              {
                "@type": "Question",
                "name": "How many sessions will I need for anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most clients see significant improvements within 4-6 sessions for anxiety and stress. The number varies based on your individual circumstances, the severity of symptoms, and your goals. We'll discuss your specific needs during the free initial consultation and create a tailored treatment plan. I offer a discounted 4-session package which provides better value and ensures commitment to the process."
                }
              },
              {
                "@type": "Question",
                "name": "Does hypnotherapy work for panic attacks?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is highly effective for panic attacks. It works by calming your overactive fight-or-flight response, helping you understand the triggers, and teaching you practical techniques to manage panic symptoms when they arise. Most clients notice a significant reduction in both the frequency and intensity of panic attacks within 4-6 sessions. The solution-focused approach addresses the underlying anxiety that drives panic attacks rather than just managing symptoms."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Anxiety and Stress Treatment for Craigavon
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Professional hypnotherapy serving Craigavon, Lurgan, and Portadown from Belfast
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">25 miles</span> from Craigavon via M1 • <span className="font-semibold">30-35 min drive</span>
                </p>
              </div>
              <p className="text-gray-600 mt-2">Online sessions also available for Craigavon residents</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Anxiety in Craigavon */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Understanding Anxiety and Stress in Craigavon
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                If you're living in Craigavon, Lurgan, or Portadown and struggling with anxiety or stress, you're not alone. As the largest town in this phase of expansion with over 67,000 residents, Craigavon presents unique challenges that can significantly impact mental health. The planned "new town" development, created in 1965 to link Lurgan and Portadown, offers housing and employment opportunities but often lacks the organic community connections and cohesive identity that traditional towns provide naturally.
              </p>
              <p>
                Many Craigavon residents experience anxiety related to the car-dependent sprawl, the absence of a traditional town center, and the ongoing identity confusion between "Craigavon," "Lurgan," and "Portadown." These factors can contribute to feelings of disconnection, isolation, and not quite belonging anywhere in particular. Add to this the pressures of modern suburban living - work stress, M1 commutes to Belfast, dual-income family responsibilities, retail shift work at Rushmere Shopping Centre, and financial worries - and it's no wonder anxiety and stress levels can feel overwhelming.
              </p>
              <p>
                While I don't have a physical clinic in Craigavon itself, I'm proud to serve the Craigavon borough community from my Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast. The journey from Craigavon is straightforward - approximately 25 miles north via the M1 motorway, taking just 30-35 minutes from Junction 10. For residents of Craigavon, Lurgan, and Portadown, this provides accessible specialist anxiety and stress treatment at a manageable distance that makes regular in-person sessions entirely practical.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Anxiety Affects You */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Anxiety and Stress Affect Craigavon Residents
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">Common Anxiety Symptoms</h3>
                <ul className="space-y-3">
                  {[
                    'Difficulty falling or staying asleep',
                    'Racing thoughts, especially at night',
                    'Physical symptoms: tight chest, pounding heart, tense shoulders',
                    'Constant sense of unease or dread',
                    'Avoiding situations out of fear or worry',
                    'Feeling overwhelmed by everyday tasks',
                    'Difficulty concentrating or making decisions',
                    'Irritability and mood swings'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">Craigavon-Specific Stressors</h3>
                <ul className="space-y-3">
                  {[
                    'M1 commuter stress and traffic anxiety',
                    'Work-life balance struggles with Belfast commute',
                    'Retail and shift work pressures at Rushmere',
                    'Financial anxiety from dual-income demands',
                    'Social isolation in car-dependent sprawl',
                    'Lack of community connection and belonging',
                    'Identity confusion (Craigavon/Lurgan/Portadown)',
                    'Family responsibilities and time pressures'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-accent-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 mt-6">
              <p className="text-gray-700 text-lg">
                When stress and anxiety take over, it can feel impossible to switch off. But there is hope - you can break the cycle and regain control. Solution-focused hypnotherapy offers an evidence-based approach that addresses both the symptoms and the underlying causes of anxiety.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Hypnotherapy Helps with Anxiety and Stress
            </h2>
            <p className="text-gray-700 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Solution-focused hypnotherapy gently shifts focus away from worry and toward practical, positive change. Hypnosis calms the overactive fight-or-flight response so panic and tension reduce, and clarity returns.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {[
                {
                  icon: <Heart className="w-8 h-8 text-accent-600" />,
                  title: 'Calm the Nervous System',
                  text: 'Soothe the overactive stress response and reduce physical anxiety symptoms like racing heart, tight chest, and tense muscles. Your body learns to relax again.'
                },
                {
                  icon: <Brain className="w-8 h-8 text-accent-600" />,
                  title: 'Rewire Thought Patterns',
                  text: 'Build new habits of thought and behavior that support resilience and wellbeing. Replace anxious thinking with calmer, more helpful responses to stress.'
                },
                {
                  icon: <Moon className="w-8 h-8 text-accent-600" />,
                  title: 'Restore Restful Sleep',
                  text: 'Create a calmer pre-sleep routine and quiet the racing mind. Wake feeling more refreshed and better equipped to handle daily challenges.'
                }
              ].map(({ icon, title, text }) => (
                <div key={title} className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4">{icon}</div>
                    <h3 className="font-heading font-semibold text-gray-900 mb-3 text-xl">{title}</h3>
                    <p className="text-gray-700">{text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">What Hypnotherapy Can Help With</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <ul className="space-y-2">
                  {[
                    'Generalized anxiety disorder',
                    'Social anxiety and isolation',
                    'Panic attacks and panic disorder',
                    'Work-related stress and burnout'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <ul className="space-y-2">
                  {[
                    'Commuter stress and travel anxiety',
                    'Sleep problems and insomnia',
                    'Performance anxiety and exam stress',
                    'Family and relationship stress'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Sharon */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Why Choose Sharon Clare Hypnotherapy for Craigavon?
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  CNHC Registered & Nursing Background
                </h3>
                <p className="text-gray-700">
                  As a CNHC registered clinical hypnotherapist with 15 years of nursing experience at Belfast City Hospital, I bring professional healthcare expertise to every anxiety treatment session. You can trust that you're receiving evidence-based treatment from a qualified professional who understands medical contexts, maintains strict ethical standards, and approaches your care with the same professionalism you'd expect from any healthcare provider.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Understanding New Town Challenges
                </h3>
                <p className="text-gray-700">
                  Living in a planned development like Craigavon brings unique psychological challenges that historic towns don't face. The lack of an organic community center, the car-dependent sprawl, the confusion between Craigavon/Lurgan/Portadown identities - these factors can contribute to anxiety, feelings of disconnection, and a sense of not quite belonging. I understand that where we live profoundly affects our mental health.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Accessible from Craigavon Borough
                </h3>
                <p className="text-gray-700">
                  At just 25 miles via the M1 motorway (30-35 minutes drive), my Belfast clinic is genuinely accessible for regular anxiety treatment appointments from Craigavon, Lurgan, and Portadown. The M1 is a high-quality dual carriageway that makes for a straightforward journey. Many clients find the drive therapeutic - dedicated time away from daily pressures to focus on yourself and your wellbeing.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Flexible Online Sessions Available
                </h3>
                <p className="text-gray-700">
                  Can't make the journey to Belfast on a particular week? Prefer the convenience of attending from home? Secure online sessions via video call provide the same professional anxiety treatment from the comfort of your own home in Craigavon. This option is ideal for clients with caring responsibilities, shift workers, or anyone who simply prefers the flexibility of online sessions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting to Belfast from Craigavon */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Getting to Belfast from Craigavon
            </h2>
            <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Car (Recommended)</h3>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Route:</span> Take the M1 motorway from Junction 10 (Craigavon) into Belfast, then south along the Ormeau Road (A24)
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Distance:</span> 25 miles through Moira and Lisburn
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Time:</span> 30-35 minutes in typical traffic
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Destination:</span> Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">By public transport:</span> Botanic and City Hospital are the nearest train stations, with Lanyon Place serving regional routes, and Metro buses run along the Ormeau Road corridor
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Practical and Manageable</h3>
                <p className="text-gray-700">
                  At 25 miles via the M1 motorway, the journey from Craigavon to Belfast is genuinely practical for regular anxiety treatment appointments. Many Craigavon clients find the journey manageable and worthwhile for specialist care. Some even share that the drive becomes therapeutic - dedicated time away from daily pressures to focus on yourself and your wellbeing.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 border-2 border-accent-200">
              <h3 className="font-semibold text-gray-900 mb-4 text-lg">Online Sessions Alternative</h3>
              <p className="text-gray-700">
                Prefer not to drive to Belfast? I offer secure online hypnotherapy sessions via video call. Online sessions provide the same professional anxiety treatment with the convenience of attending from your home in Craigavon, Lurgan, or Portadown. This option is ideal for those with mobility concerns, caring responsibilities, shift work patterns, or anyone who prefers the flexibility of online therapy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Craigavon Area Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I was having panic attacks almost daily - the commute to Belfast for work, the financial pressure, feeling isolated in Portadown despite living around thousands of people. Sharon helped me understand what was happening in my brain and gave me tools to manage the anxiety. After five sessions, the panic attacks have stopped, I'm sleeping properly again, and I actually feel calm. The drive to Belfast for therapy became part of my self-care routine rather than another stressor."
                </p>
                <p className="font-semibold text-gray-900">S.K., Portadown - Anxiety and Panic Attacks</p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Working retail at Rushmere Shopping Centre with the shift patterns and difficult customers, I was constantly stressed and anxious. I couldn't switch off, even at home. Sharon's hypnotherapy approach helped me manage the work stress and build resilience. I'm still in the same job, but I handle it completely differently now. The anxiety doesn't control me anymore. I chose online sessions because of my irregular hours and it worked brilliantly."
                </p>
                <p className="font-semibold text-gray-900">L.P., Craigavon - Work Stress and Anxiety</p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I'd struggled with social anxiety for years, feeling disconnected and like I didn't belong anywhere in Lurgan. Sharon helped me understand that it wasn't about where I lived, it was about how I felt about myself. After six sessions, I'm so much more confident. I've actually started joining community groups and making real connections. The anxiety is still there sometimes, but it doesn't stop me living my life anymore."
                </p>
                <p className="font-semibold text-gray-900">M.H., Lurgan - Social Anxiety and Disconnection</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Craigavon Anxiety Treatment
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is Belfast from Craigavon for anxiety treatment?
                </h3>
                <p className="text-gray-700">
                  Belfast is approximately 25 miles from Craigavon via the M1 motorway, taking 30-35 minutes by car. This manageable distance makes regular anxiety treatment appointments entirely practical. Many Craigavon clients find the drive therapeutic - a chance to leave daily stressors behind and transition into a calm, focused mindset before their session.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with commuter stress and anxiety?
                </h3>
                <p className="text-gray-700">
                  Yes, solution-focused hypnotherapy is highly effective for commuter-related stress and anxiety. Many Craigavon residents commute to Belfast for work, and the daily M1 journey, traffic pressures, and work-life balance challenges can trigger significant anxiety. Hypnotherapy helps by calming your stress response, building resilience, improving sleep quality, and developing healthier thought patterns around work pressures.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Do you offer online anxiety treatment for Craigavon residents?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. I offer secure online hypnotherapy sessions via video call for Craigavon residents who prefer the convenience of online therapy or cannot make the drive to Belfast on a particular week. Online sessions provide the same professional, evidence-based anxiety treatment as in-person sessions. This option is ideal for clients with caring responsibilities, shift work patterns, or those who want maximum flexibility.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with feeling disconnected or not belonging?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy can be very effective for feelings of disconnection and social anxiety. Living in planned developments like Craigavon, which lack organic community centers, can contribute to isolation and a sense of not quite belonging. While hypnotherapy cannot change your environment, it can help reduce social anxiety, build confidence in forming connections, develop resilience, and create a stronger sense of self-worth that isn't entirely dependent on external community validation.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How many sessions will I need for anxiety?
                </h3>
                <p className="text-gray-700">
                  Most clients see significant improvements within 4-6 sessions for anxiety and stress. The number varies based on your individual circumstances, the severity of symptoms, and your goals. We'll discuss your specific needs during the free initial consultation and create a tailored treatment plan. I offer a discounted 4-session package which provides better value and ensures commitment to the process.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does hypnotherapy work for panic attacks?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is highly effective for panic attacks. It works by calming your overactive fight-or-flight response, helping you understand the triggers, and teaching you practical techniques to manage panic symptoms when they arise. Most clients notice a significant reduction in both the frequency and intensity of panic attacks within 4-6 sessions. The solution-focused approach addresses the underlying anxiety that drives panic attacks rather than just managing symptoms.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA
        title="Start Your Journey to Calm"
        subtitle="Take the first step toward less anxiety, less stress, and more peace. Book a free consultation today and discover how hypnotherapy can help you regain control and find calm in the chaos."
        primaryButtonText="Book Free Consultation"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default CraigavonAnxietyStress;
